"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";

import LegalPageShell from "@/components/sections/LegalPageShell";
import CompanyIdentityBlock, { LegalDocLinksList } from "@/components/sections/CompanyIdentityBlock";
import { getLegalContent } from "@/content/legal";
import { useLocale } from "@/lib/i18n/provider";
import { localizePath } from "@/lib/i18n/routing";

export default function LegalHub() {
  const { locale } = useLocale();
  const legal = getLegalContent(locale);
  const hub = legal.hub;

  return (
    <LegalPageShell>
      <div className="mx-auto w-full max-w-4xl px-5 pb-20 pt-28 md:px-7 md:pb-24 md:pt-36">
        <Link
          href={localizePath("/", locale)}
          className="inline-flex items-center gap-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
        >
          <ArrowLeft size={14} />
          {legal.ui.backToHomeLabel}
        </Link>

        <div className="mt-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {hub.eyebrow}
          </p>
          <h1 className="mt-4 text-[clamp(2rem,4.8vw,3.2rem)] font-bold leading-[1.05] tracking-[-0.03em] text-[var(--color-text-primary)]">
            {hub.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[var(--color-text-secondary)]">
            {hub.intro}
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-[1.2fr_1fr]">
          <section>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{hub.documentsTitle}</h2>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{hub.documentsBody}</p>
            <LegalDocLinksList />
          </section>

          <CompanyIdentityBlock />
        </div>

        <section className="mt-10 rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] p-6 md:p-8">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{hub.rightsTitle}</h2>
          <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">{hub.rightsIntro}</p>
          <ul className="mt-4 grid gap-2 text-sm text-[var(--color-text-secondary)] sm:grid-cols-2">
            {hub.rightsItems.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent-base)]" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-[var(--color-text-secondary)]">
            {hub.rightsContactPrefix}{" "}
            <a
              href={`mailto:${hub.rightsEmail}`}
              className="font-medium text-[var(--color-text-primary)] transition-colors hover:text-[var(--color-accent-base)]"
            >
              {hub.rightsEmail}
            </a>
            {hub.rightsAppSuffix ? `. ${hub.rightsAppSuffix}` : null}
          </p>
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
            {hub.aepdPrefix}{" "}
            <a
              href={hub.aepdUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[var(--color-text-primary)] transition-colors hover:text-[var(--color-accent-base)]"
            >
              {hub.aepdLabel}
            </a>
          </p>
        </section>
      </div>
    </LegalPageShell>
  );
}
